/**
 * `/cards` feature slice — read-only list of saved payment methods.
 *
 *   loadSavedCards → BFF `GET /v1/payment-methods`
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { BffError, paymentMethodsApi } from '../../services';
import type { PaymentMethodsListResponse, SavedPaymentMethod } from '../../types/api';

export interface SavedCardsSliceError {
  code: string;
  message: string;
}

export interface SavedCardsState {
  items: SavedPaymentMethod[];
  loading: boolean;
  error: SavedCardsSliceError | null;
}

const initialState: SavedCardsState = {
  items: [],
  loading: false,
  error: null,
};

function asSliceError(error: unknown): SavedCardsSliceError {
  if (error instanceof BffError) return { code: error.code, message: error.message };
  if (error instanceof Error) return { code: 'unknown_error', message: error.message };
  return { code: 'unknown_error', message: 'Unknown error' };
}

export const loadSavedCards = createAsyncThunk<
  PaymentMethodsListResponse,
  { tokenId: string; view?: 'smarthq' },
  { rejectValue: SavedCardsSliceError }
>('savedCards/loadSavedCards', async (input, { rejectWithValue }) => {
  try {
    return await paymentMethodsApi.list(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

const savedCardsSlice = createSlice({
  name: 'savedCards',
  initialState,
  reducers: {
    resetSavedCards(): SavedCardsState {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSavedCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loadSavedCards.fulfilled,
        (state, action: PayloadAction<PaymentMethodsListResponse>) => {
          state.loading = false;
          state.items = action.payload.paymentMethods;
        },
      )
      .addCase(loadSavedCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          code: 'unknown_error',
          message: 'loadSavedCards failed',
        };
      });
  },
});

export const { resetSavedCards } = savedCardsSlice.actions;
export default savedCardsSlice.reducer;
